/**
 * 会员信息管理Store
 * 统一管理用户会员等级和权限信息
 */
import { defineStore } from 'pinia'
import { getCurrentMembership } from '@/api/membership'

/** 本地缓存结构版本：变更字段时必须递增，避免旧缓存缺少门控字段导致按钮误隐藏 */
const MEMBERSHIP_CACHE_VERSION = 9

const normalizeOptionalLimit = (value) => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return null
  return Math.max(Math.trunc(parsed), 0)
}

/** fetchMembership 的并发去重 Promise（模块级，不进入响应式 state） */
let _membershipFetchInflight = null

export const useMembershipStore = defineStore('membership', {
  state: () => ({
    // 会员等级信息
    tier: null,
    // 会员订阅信息
    membership: null,
    // 加载状态
    isLoading: false,
    // 最后更新时间
    lastUpdateTime: null,
    // 初始化状态
    isInitialized: false
  }),
  
  getters: {
    /**
     * 获取会员等级代码
     */
    tierCode: (state) => state.tier?.tier_code || 'free',
    
    /**
     * 获取会员等级名称
     */
    tierName: (state) => state.tier?.tier_name || 'Free',
    
    /**
     * 获取会员等级层级
     */
    tierLevel: (state) => state.tier?.tier_level || 0,
    
    /**
     * 会员是否激活
     */
    isActive: (state) => state.membership?.is_active || false,
    
    /**
     * 是否启用幻觉优化
     */
    hallucinationOptimization: (state) => state.tier?.hallucination_optimization || false,

    /**
     * 会员等级是否开放情报订阅（与菜单权限配合）
     */
    canUseIntelligenceSubscriptionTier: (state) =>
      Boolean(state.tier?.can_use_intelligence_subscription),

    canViewIntelligenceFeedTier: (state) =>
      Boolean(state.tier?.can_view_intelligence_feed),
    
    /**
     * 是否可以下载PDF报告
     */
    canDownloadPdf: (state) => {
      if (!state.tier) return false
      return state.tier.can_download_pdf_report !== false
    },

    /**
     * 是否可以下载 Word 报告（含兼容字段）
     */
    canDownloadWord: (state) => {
      const tier = state.tier
      if (!tier) return false
      if (tier.can_download_word_report != null) return Boolean(tier.can_download_word_report)
      return Boolean(tier.can_download_word_md_report)
    },

    /**
     * 是否可以下载 Markdown 报告（含兼容字段）
     */
    canDownloadMd: (state) => {
      const tier = state.tier
      if (!tier) return false
      if (tier.can_download_md_report != null) return Boolean(tier.can_download_md_report)
      return Boolean(tier.can_download_word_md_report)
    },

    /**
     * 是否可以下载 HTML 报告
     */
    canDownloadHtml: (state) => Boolean(state.tier?.can_download_html_report),

    /**
     * 是否可下载任意格式报告（用于显示下载入口）
     */
    canDownloadAnyReport: (state) => {
      const tier = state.tier
      if (!tier) return false
      const canPdf = tier.can_download_pdf_report !== false
      const canWord = tier.can_download_word_report != null
        ? Boolean(tier.can_download_word_report)
        : Boolean(tier.can_download_word_md_report)
      const canMd = tier.can_download_md_report != null
        ? Boolean(tier.can_download_md_report)
        : Boolean(tier.can_download_word_md_report)
      const canHtml = Boolean(tier.can_download_html_report)
      return canPdf || canWord || canMd || canHtml
    },

    /**
     * 兼容：Word/MD 合并权限（任一即可）
     */
    canDownloadWordMd: (state) => {
      const tier = state.tier
      if (!tier) return false
      const canWord = tier.can_download_word_report != null
        ? Boolean(tier.can_download_word_report)
        : Boolean(tier.can_download_word_md_report)
      const canMd = tier.can_download_md_report != null
        ? Boolean(tier.can_download_md_report)
        : Boolean(tier.can_download_word_md_report)
      return canWord || canMd
    },
    
    /**
     * 获取可用的AI模型列表
     */
    availableModels: (state) => state.tier?.available_models || [],
    
    /**
     * 是否启用网络搜索
     */
    enableWebsearch: (state) => state.tier?.enable_websearch !== false,
    
    /**
     * 获取单个文件最大上传大小(MB)
     */
    maxUploadFileSizeMb: (state) => state.tier?.max_upload_file_size_mb || 20,
    
    /**
     * 获取允许上传的文件后缀列表
     */
    allowedUploadExtensions: (state) => {
      const extensions = state.tier?.allowed_upload_extensions
      if (extensions && Array.isArray(extensions) && extensions.length > 0) {
        return extensions
      }
      // 默认允许的文件类型（13 种，与系统/会员默认对齐）
      return [
        '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
        '.txt', '.md', '.markdown', '.html', '.htm', '.csv'
      ]
    },
    
    /**
     * 获取完整的会员信息（用于localStorage）
     */
    membershipInfo: (state) => {
      if (!state.tier) return null
      return {
        _v: MEMBERSHIP_CACHE_VERSION,
        tier_code: state.tier.tier_code,
        tier_name: state.tier.tier_name,
        tier_level: state.tier.tier_level,
        is_active: state.membership?.is_active || false,
        hallucination_optimization: state.tier.hallucination_optimization || false,
        can_use_intelligence_subscription: Boolean(state.tier.can_use_intelligence_subscription),
        can_view_intelligence_feed: Boolean(state.tier.can_view_intelligence_feed),
        can_use_report_interactive_edit: Boolean(state.tier.can_use_report_interactive_edit),
        can_use_report_selection_qa: Boolean(state.tier.can_use_report_selection_qa),
        can_use_report_selection_regenerate: Boolean(state.tier.can_use_report_selection_regenerate),
        can_use_report_selection_add_to_map: Boolean(state.tier.can_use_report_selection_add_to_map),
        can_use_knowledge_map_llm_wiki: Boolean(state.tier.can_use_knowledge_map_llm_wiki),
        can_download_pdf_report: state.tier.can_download_pdf_report !== false,
        can_download_word_md_report: state.tier.can_download_word_md_report || false,
        can_download_word_report: Boolean(state.tier.can_download_word_report),
        can_download_md_report: Boolean(state.tier.can_download_md_report),
        can_download_html_report: Boolean(state.tier.can_download_html_report),
        enable_websearch: state.tier.enable_websearch !== false,
        max_upload_file_size_mb: state.tier.max_upload_file_size_mb || 20,
        allowed_upload_extensions: Array.isArray(state.tier.allowed_upload_extensions)
          ? state.tier.allowed_upload_extensions
          : [],
        can_use_knowledge_base: Boolean(state.tier.can_use_knowledge_base),
        knowledge_base_max_files: normalizeOptionalLimit(state.tier.knowledge_base_max_files),
        knowledge_base_max_files_per_task:
          normalizeOptionalLimit(state.tier.knowledge_base_max_files_per_task),
        quick_mode_max_files_per_task:
          state.tier.quick_mode_max_files_per_task === null ||
          state.tier.quick_mode_max_files_per_task === undefined
            ? null
            : Number(state.tier.quick_mode_max_files_per_task) || 0,
        quick_mode_max_upload_file_size_mb:
          state.tier.quick_mode_max_upload_file_size_mb === null ||
          state.tier.quick_mode_max_upload_file_size_mb === undefined
            ? null
            : Number(state.tier.quick_mode_max_upload_file_size_mb) || 0,
        max_concurrent_tasks: Number(state.tier.max_concurrent_tasks) || 1,
        max_reports_per_month:
          state.tier.max_reports_per_month === null ||
          state.tier.max_reports_per_month === undefined
            ? null
            : Number(state.tier.max_reports_per_month),
        available_models: Array.isArray(state.tier.available_models)
          ? [...state.tier.available_models]
          : [],
        updated_at: new Date(state.lastUpdateTime).toISOString()
      }
    }
  },
  
  actions: {
    /**
     * 获取会员信息
     * @returns {Promise<Object>} 会员等级信息
     */
    async fetchMembership() {
      // 并发去重：报告页多个门控 composable 会同时调用本方法，
      // 复用同一个进行中的请求，避免重复请求 membership/current 接口。
      if (_membershipFetchInflight) {
        return _membershipFetchInflight
      }

      _membershipFetchInflight = (async () => {
        try {
          this.isLoading = true
          console.log('🔄 [MembershipStore] 从API获取会员信息...')

          const response = await getCurrentMembership()

          this.tier = response.tier
          this.membership = response.membership
          this.lastUpdateTime = Date.now()
          this.isInitialized = true

          // 同步到localStorage
          this._syncToLocalStorage()

          console.log('✅ [MembershipStore] 会员信息已更新:', {
            tier_code: this.tierCode,
            tier_name: this.tierName,
            hallucination_optimization: this.hallucinationOptimization
          })

          return this.tier
        } catch (error) {
          console.error('❌ [MembershipStore] 获取会员信息失败:', error)

          // 如果获取失败，尝试从localStorage恢复
          this._loadFromLocalStorage()

          throw error
        } finally {
          this.isLoading = false
          _membershipFetchInflight = null
        }
      })()

      return _membershipFetchInflight
    },
    
    /**
     * 从localStorage初始化会员信息
     */
    initFromLocalStorage() {
      try {
        const cached = localStorage.getItem('user_membership')
        if (cached) {
          const data = JSON.parse(cached)

          if (!data._v || data._v < MEMBERSHIP_CACHE_VERSION) {
            console.warn('📦 [MembershipStore] 会员本地缓存版本过旧，已清除并等待重新拉取')
            localStorage.removeItem('user_membership')
            return false
          }

          console.log('📦 [MembershipStore] 从localStorage恢复会员信息:', data.tier_code)

          // 恢复数据（须与 membershipInfo / 报告门控 composable 使用的字段一致）
          this.tier = {
            tier_code: data.tier_code,
            tier_name: data.tier_name,
            tier_level: data.tier_level,
            hallucination_optimization: Boolean(data.hallucination_optimization),
            can_use_intelligence_subscription: Boolean(data.can_use_intelligence_subscription),
            can_view_intelligence_feed: Boolean(data.can_view_intelligence_feed),
            can_use_report_interactive_edit: Boolean(data.can_use_report_interactive_edit),
            can_use_report_selection_qa: Boolean(data.can_use_report_selection_qa),
            can_use_report_selection_regenerate: Boolean(data.can_use_report_selection_regenerate),
            can_use_report_selection_add_to_map: Boolean(data.can_use_report_selection_add_to_map),
            can_use_knowledge_map_llm_wiki: Boolean(data.can_use_knowledge_map_llm_wiki),
            can_download_pdf_report: data.can_download_pdf_report,
            can_download_word_md_report: data.can_download_word_md_report,
            can_download_word_report: Boolean(data.can_download_word_report),
            can_download_md_report: Boolean(data.can_download_md_report),
            can_download_html_report: Boolean(data.can_download_html_report),
            enable_websearch: data.enable_websearch !== false,
            max_upload_file_size_mb: data.max_upload_file_size_mb || 20,
            allowed_upload_extensions: data.allowed_upload_extensions || [],
            can_use_knowledge_base: Boolean(data.can_use_knowledge_base),
            knowledge_base_max_files: normalizeOptionalLimit(data.knowledge_base_max_files),
            knowledge_base_max_files_per_task:
              normalizeOptionalLimit(data.knowledge_base_max_files_per_task),
            quick_mode_max_files_per_task:
              data.quick_mode_max_files_per_task === null ||
              data.quick_mode_max_files_per_task === undefined
                ? null
                : Number(data.quick_mode_max_files_per_task) || 0,
            quick_mode_max_upload_file_size_mb:
              data.quick_mode_max_upload_file_size_mb === null ||
              data.quick_mode_max_upload_file_size_mb === undefined
                ? null
                : Number(data.quick_mode_max_upload_file_size_mb) || 0,
            max_concurrent_tasks: Number(data.max_concurrent_tasks) || 1,
            max_reports_per_month:
              data.max_reports_per_month === null ||
              data.max_reports_per_month === undefined
                ? null
                : Number(data.max_reports_per_month),
            available_models: Array.isArray(data.available_models)
              ? [...data.available_models]
              : []
          }
          this.membership = {
            is_active: data.is_active
          }
          this.lastUpdateTime = new Date(data.updated_at).getTime()
          this.isInitialized = true
          
          return true
        }
      } catch (error) {
        console.error('❌ [MembershipStore] 从localStorage恢复失败:', error)
        localStorage.removeItem('user_membership')
      }
      
      return false
    },
    
    /**
     * 同步会员信息到localStorage
     * @private
     */
    _syncToLocalStorage() {
      try {
        if (this.membershipInfo) {
          localStorage.setItem('user_membership', JSON.stringify(this.membershipInfo))
          console.log('💾 [MembershipStore] 会员信息已同步到localStorage')
        }
      } catch (error) {
        console.error('❌ [MembershipStore] 同步到localStorage失败:', error)
      }
    },
    
    /**
     * 从localStorage加载会员信息
     * @private
     */
    _loadFromLocalStorage() {
      const success = this.initFromLocalStorage()
      if (success) {
        console.log('⚠️ [MembershipStore] 使用localStorage中的会员信息作为备用')
      } else {
        console.log('⚠️ [MembershipStore] 无可用的会员信息，使用默认值(free)')
        this._setDefaultValues()
      }
    },
    
    /**
     * 设置默认值
     * @private
     */
    _setDefaultValues() {
      this.tier = {
        tier_code: 'free',
        tier_name: 'Free',
        tier_level: 0,
        hallucination_optimization: false,
        can_use_intelligence_subscription: false,
        can_view_intelligence_feed: false,
        can_use_report_interactive_edit: false,
        can_use_report_selection_qa: false,
        can_use_report_selection_regenerate: false,
        can_use_report_selection_add_to_map: false,
        can_use_knowledge_base: false,
        knowledge_base_max_files: 0,
        knowledge_base_max_files_per_task: 0,
        quick_mode_max_files_per_task: null,
        quick_mode_max_upload_file_size_mb: null,
        max_concurrent_tasks: 1,
        max_reports_per_month: null,
        available_models: [],
        can_download_pdf_report: true,
        can_download_word_md_report: false,
        can_download_word_report: false,
        can_download_md_report: false,
        can_download_html_report: false,
        enable_websearch: true,
        max_upload_file_size_mb: 20,
        allowed_upload_extensions: [
          '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
          '.txt', '.csv', '.html', '.htm', '.md', '.markdown'
        ]
      }
      this.membership = {
        is_active: true
      }
      this.lastUpdateTime = Date.now()
      this.isInitialized = true
    },
    
    /**
     * 清除会员信息
     */
    clearMembership() {
      console.log('🧹 [MembershipStore] 清除会员信息')

      // 重置进行中的请求：避免登出/切换用户时，旧请求返回后把旧数据写回
      _membershipFetchInflight = null

      this.tier = null
      this.membership = null
      this.lastUpdateTime = null
      this.isInitialized = false
      
      // 清除localStorage
      try {
        localStorage.removeItem('user_membership')
      } catch (error) {
        console.error('❌ [MembershipStore] 清除localStorage失败:', error)
      }
    },
    
    /**
     * 检查是否有特定权限
     * @param {string} permission - 权限名称
     * @returns {boolean}
     */
    hasPermission(permission) {
      const permissions = {
        'hallucination_optimization': this.hallucinationOptimization,
        'download_pdf': this.canDownloadPdf,
        'download_word': this.canDownloadWord,
        'download_md': this.canDownloadMd,
        'download_html': this.canDownloadHtml,
        'download_word_md': this.canDownloadWordMd
      }
      
      return permissions[permission] || false
    }
  }
})
